import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { fonts } from "../../styles/theme";
import { cores } from "../relatorio/styles";
import type { PerfilDoCliente } from "../../utils/perfis-do-cliente";

const Envolve = styled.div`
  position: relative;
  min-width: 0;
`;

const Gatilho = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  /* No celular sobra pouco ao lado do logo: o nome encolhe com reticências. */
  max-width: min(calc(100vw - 196px), 300px);
  padding: 8px 10px 8px 14px;
  border: 1px solid #dadce0;
  border-radius: 1000px;
  background: #ffffff;
  color: ${cores.ink};
  font-family: ${fonts.gilroySemiBold}, system-ui, sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover,
  &[aria-expanded="true"] {
    background: ${cores.grayTint};
  }

  .nome {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .material-icons {
    font-size: 20px;
    color: ${cores.ink3};
  }
`;

const Painel = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  z-index: 50;
  width: min(320px, calc(100vw - 32px));
  max-height: min(70vh, 520px);
  overflow-y: auto;
  padding: 8px;
  border: 1px solid ${cores.line};
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 12px 32px rgba(32, 33, 36, 0.16);
`;

const Grupo = styled.div`
  padding: 10px 12px 6px;
  font-family: ${fonts.gilroySemiBold}, system-ui, sans-serif;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${cores.ink4};
`;

const Item = styled.button<{ $atual?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 10px 12px;
  border: 0;
  border-radius: 14px;
  background: ${({ $atual }) => ($atual ? cores.blueTint : "transparent")};
  color: ${({ $atual }) => ($atual ? cores.blueDark : cores.ink)};
  font-family: ${({ $atual }) => ($atual ? fonts.gilroySemiBold : fonts.gilroyRegular)}, system-ui, sans-serif;
  font-size: 15px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ $atual }) => ($atual ? cores.blueTint : cores.grayTint)};
  }

  .material-icons {
    font-size: 20px;
    color: ${({ $atual }) => ($atual ? cores.blue : cores.ink3)};
  }

  .texto {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .nome {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .nivel {
    font-size: 12px;
    color: ${cores.ink4};
    font-family: ${fonts.gilroyRegular}, system-ui, sans-serif;
  }
`;

const Divisor = styled.hr`
  margin: 6px 4px;
  border: 0;
  border-top: 1px solid ${cores.line};
`;

type Props = {
  perfis: PerfilDoCliente[];
  atual: string;
  nomeAtual?: string;
  aoTrocar: (perfil: PerfilDoCliente) => void;
  aoAbrirPainel: () => void;
  aoSair: () => void;
};

/**
 * Topo do relatório na tela do cliente: mostra a empresa aberta e, para quem
 * tem mais de uma, troca de empresa sem voltar ao login (no app instalado não
 * há barra de endereço para chegar a /select-profile).
 */
export const MenuDoCliente = ({ perfis, atual, nomeAtual, aoTrocar, aoAbrirPainel, aoSair }: Props) => {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const variasEmpresas = perfis.length > 1;
  const rotulo = nomeAtual || perfis.find((perfil) => perfil.id === atual)?.nome || "Minha empresa";

  useEffect(() => {
    if (!aberto) return;
    const fora = (evento: MouseEvent | TouchEvent) => {
      if (caixa.current && !caixa.current.contains(evento.target as Node)) setAberto(false);
    };
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") setAberto(false);
    };
    document.addEventListener("mousedown", fora);
    document.addEventListener("touchstart", fora);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("touchstart", fora);
      document.removeEventListener("keydown", tecla);
    };
  }, [aberto]);

  const escolher = (perfil: PerfilDoCliente) => {
    setAberto(false);
    if (perfil.id !== atual) aoTrocar(perfil);
  };

  return (
    <Envolve ref={caixa}>
      <Gatilho
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-label={variasEmpresas ? `Empresa aberta: ${rotulo}. Trocar de empresa` : "Menu da conta"}
        onClick={() => setAberto((valor) => !valor)}
      >
        <span className="material-icons" aria-hidden="true">{variasEmpresas ? "storefront" : "account_circle"}</span>
        <span className="nome">{variasEmpresas ? rotulo : "Minha conta"}</span>
        <span className="material-icons" aria-hidden="true">{aberto ? "expand_less" : "expand_more"}</span>
      </Gatilho>

      {aberto && (
        <Painel role="menu">
          {variasEmpresas && (
            <>
              <Grupo>{`Suas empresas (${perfis.length})`}</Grupo>
              {perfis.map((perfil) => {
                const ehAtual = perfil.id === atual;
                return (
                  <Item key={perfil.id} type="button" role="menuitemradio" aria-checked={ehAtual} $atual={ehAtual} onClick={() => escolher(perfil)}>
                    <span className="material-icons" aria-hidden="true">{ehAtual ? "check_circle" : "storefront"}</span>
                    <span className="texto">
                      <span className="nome">{perfil.nome || "Empresa sem nome"}</span>
                      {perfil.nivel === "VIEW" && <span className="nivel">Só visualização</span>}
                    </span>
                  </Item>
                );
              })}
              <Divisor />
            </>
          )}
          <Item type="button" role="menuitem" onClick={() => { setAberto(false); aoAbrirPainel(); }}>
            <span className="material-icons" aria-hidden="true">tune</span>
            <span className="texto"><span className="nome">Área do cliente</span></span>
          </Item>
          <Item type="button" role="menuitem" onClick={() => { setAberto(false); aoSair(); }}>
            <span className="material-icons" aria-hidden="true">logout</span>
            <span className="texto"><span className="nome">Sair</span></span>
          </Item>
        </Painel>
      )}
    </Envolve>
  );
};
